import { useEffect, useRef, useState } from 'react';
import type { ThemeEvent } from './themeContext';

const KEY = 'wise-friend-theme-completions-v1';
const LIMIT = 100;
const EMPTY_EVENTS: ThemeEvent[] = [];
/** Opaque navigation token; contains no learner, assessment or run identifiers. */
export const createThemeCompletionId = () => typeof globalThis.crypto?.randomUUID === 'function'
  ? globalThis.crypto.randomUUID()
  : `${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;

/** Claim after React commits. Fail closed when session storage is unavailable. */
function claimCompletion(id: string): boolean {
  if (!/^[a-zA-Z0-9-]{16,80}$/.test(id)) return false;
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(KEY) || '[]');
    const previous = Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string').slice(-LIMIT) : [];
    if (previous.includes(id)) return false;
    sessionStorage.setItem(KEY, JSON.stringify([...previous, id].slice(-LIMIT)));
    return true;
  } catch { return false; }
}

/** Fresh submit navigation only. Browser reload/history retains the token but cannot replay it. */
export function useThemeCompletionEvents(id: string | undefined, complete: boolean, moduleKey: string, earnedMilestone: boolean): ThemeEvent[] {
  const handled = useRef<string | null>(null);
  const [claimed, setClaimed] = useState<{ token: string | null; events: ThemeEvent[] }>({ token: null, events: EMPTY_EVENTS });
  useEffect(() => {
    if (!id || !complete || !moduleKey || handled.current === id) return;
    handled.current = id;
    if (!claimCompletion(id)) { setClaimed({ token: id, events: EMPTY_EVENTS }); return; }
    setClaimed({ token: id, events: [
      { id: `${id}:module`, type: 'module-completed', key: moduleKey },
      ...(earnedMilestone ? [{ id: `${id}:milestone`, type: 'milestone-reached' as const, key: moduleKey }] : []),
    ] });
  }, [id, complete, moduleKey, earnedMilestone]);
  return id && complete && claimed.token === id ? claimed.events : EMPTY_EVENTS;
}
